import { cva, type VariantProps } from 'class-variance-authority'
import { useId } from 'react'
import { Link } from 'react-router'
import { cn } from '#app/utils/misc.tsx'

const logoVariants = cva('inline-flex items-center', {
	variants: {
		size: {
			sm: 'gap-2 text-[22px]',
			default: 'gap-2.5 text-[28px]',
			lg: 'gap-3 text-[36px]',
		},
	},
	defaultVariants: { size: 'default' },
})

const pxFor: Record<'sm' | 'default' | 'lg', number> = {
	sm: 22,
	default: 28,
	lg: 36,
}

export type LogoVariant = VariantProps<typeof logoVariants>

export function Logo({
	size = 'default',
	className,
	color,
	hideWordmark = false,
	asLink = true,
	monoColor,
}: LogoVariant & {
	className?: string
	color?: string
	hideWordmark?: boolean
	asLink?: boolean
	monoColor?: string
}) {
	const px = pxFor[size ?? 'default']
	const wordmarkSize = Math.round(px * 0.95)
	const mark = <LogoMark size={px} monoColor={monoColor} />
	const wordmark = !hideWordmark && (
		<span
			className="font-display leading-none tracking-[-0.02em]"
			style={{
				fontSize: wordmarkSize,
				color: color ?? 'var(--ink)',
				fontWeight: 450,
			}}
		>
			Vigilio
		</span>
	)
	const content = (
		<>
			{mark}
			{wordmark}
		</>
	)
	if (!asLink) {
		return (
			<span className={cn(logoVariants({ size }), className)}>{content}</span>
		)
	}
	return (
		<Link to="/" className={cn(logoVariants({ size }), className)}>
			{content}
		</Link>
	)
}

function LogoMark({ size, monoColor }: { size: number; monoColor?: string }) {
	const uid = useId().replace(/:/g, '')
	const gradId = `vigilio-v-${uid}`
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 32 32"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			style={{ flex: 'none' }}
		>
			<defs>
				<linearGradient
					id={gradId}
					x1="4"
					y1="3"
					x2="28"
					y2="29"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor={monoColor ?? '#7B5CFF'} />
					<stop offset="1" stopColor={monoColor ?? '#3F1CB8'} />
				</linearGradient>
			</defs>
			{/* Left blade — lighter */}
			<path
				d="M4.5 4 L13 4 L17 18 L15 24 Z"
				fill={monoColor ?? `url(#${gradId})`}
				opacity={monoColor ? 0.85 : 1}
			/>
			{/* Right blade — darker, overlapping for depth */}
			<path
				d="M27.5 4 L19 4 L15 24 L17 29 L27.5 4 Z"
				fill={monoColor ?? `url(#${gradId})`}
			/>
		</svg>
	)
}
